import React from 'react';
import BrainLogo from '../components/BrainLogo';

/**
 * Privacy policy.
 *
 * Every factual claim here was checked against the code rather than written
 * from a template: the field list comes from models.py, the third parties from
 * the actual API calls, and the deletion behaviour from the delete_account
 * endpoint. If the app changes, this page has to change with it — a privacy
 * policy that drifts from what the software does is worse than none, because
 * it is a false statement rather than a missing one.
 */

const EFFECTIVE = 'August 16, 2026';

export default function Privacy({ navigate }) {
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#b4b4b4' }}>
      <nav style={{
        maxWidth: 720, margin: '0 auto',
        padding: '22px 24px', display: 'flex', alignItems: 'center',
      }}>
        <button
          onClick={() => navigate('landing')}
          style={{
            display: 'flex', alignItems: 'center', gap: 9,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <BrainLogo size={28} color="white" />
          <span style={{ color: '#ececec', fontWeight: 600, fontSize: 16, fontFamily: 'Inter, sans-serif' }}>
            Mindris
          </span>
        </button>
      </nav>

      <div className="manifesto-body" style={{
        maxWidth: 720, margin: '0 auto', padding: '16px 24px 110px',
        fontFamily: 'Inter, sans-serif', fontSize: 15.5, lineHeight: 1.85,
      }}>
        <h1 style={{
          fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 700, color: '#ffffff',
          letterSpacing: '-1px', fontFamily: 'Lora, Georgia, serif',
          margin: '24px 0 8px',
        }}>
          Privacy policy
        </h1>
        <p style={{ color: '#6b6b6b', fontSize: 13, marginBottom: 40 }}>
          Effective {EFFECTIVE}
        </p>

        <p className="pull">
          Mindris holds detailed information about a person who often cannot
          speak for themselves. We have written this plainly so you can decide
          whether you are comfortable with that.
        </p>

        <p>
          If you do not agree with the way data is handled and used by Mindris,
          please do not download, use or otherwise access the application. We
          provide this application with the hope that it will be helpful. It is
          completely optional and provided to you at no cost. Accordingly, we
          cannot be responsible for and disclaim any liabilities that may arise
          out of your use of the application, including any liability
          associated with data you enter.
        </p>

        <H>Who we are</H>
        <p>
          Mindris is operated from Austin, Texas. You can reach us at{' '}
          <A href="mailto:tylerkim1020@gmail.com">tylerkim1020@gmail.com</A> with
          any question about this policy or your data.
        </p>

        <H>What we collect</H>
        <p><B>Your account.</B> Your email address, your name, and a
        cryptographically hashed version of your password. We never store your
        password itself and cannot recover it.</p>

        <p><B>The care profile you create.</B> This is the potentially
        sensitive part you provide at your discretion. If you elect to do so, it
        includes the person's name, age, diagnosis, communication style, sensory
        profile, known triggers, early escalation signs, things that make
        situations worse, calming strategies, comfort items, trusted people,
        what particular behaviours mean, daily routine, medical notes, and
        recent context. When creating a profile, we ask that you use a pseudonym
        rather than the person's real name. Using a pseudonym will not diminish
        the application's functionality, and it reduces what could be exposed if
        data were ever accessed without authorisation. It does not make the
        profile anonymous: fields such as medical notes are free text, and may
        contain identifying detail if you enter it. What you provide is at your
        discretion, and we are not responsible for any personally identifiable
        information you choose to provide.</p>

        <p><B>What you describe during a session.</B> The situation you type or
        speak, the severity you select, the guidance the system returns, and a
        numeric representation of that text used to find similar past
        situations.</p>

        <p><B>Your ratings.</B> Whether a response helped, and how you rated
        individual strategies.</p>

        <p><B>Voice input.</B> If you speak instead of typing, the recording is
        sent for transcription and is <B>not stored</B> — not by us, and not on
        your device. Only the resulting text is kept, as part of the session.</p>

        <H>What we do not collect</H>
        <p>
          No location. No contacts. No photos. No camera access. No advertising
          identifiers. Mindris contains no analytics tools, no advertising SDKs,
          and no third-party trackers of any kind. We do not track you across
          other apps or websites.
        </p>

        <H>Who we share it with</H>
        <p className="pull">
          Your profile and the situation you describe are sent to OpenAI to
          generate guidance. This is the most important sentence in this policy.
        </p>
        <p>
          Mindris cannot work without doing this. The whole point is that advice
          is built around one specific person, which means the details about
          that person have to reach the model producing the advice.
        </p>
        <p>Specifically:</p>
        <ul style={{ paddingLeft: 22, margin: '0 0 30px' }}>
          <li style={{ marginBottom: 10 }}>
            <B>OpenAI</B> receives the care profile and your description of the
            situation each time you ask for guidance, plus any audio you record
            for transcription. OpenAI does not use data submitted through its
            API to train its models.
          </li>
          <li style={{ marginBottom: 10 }}>
            <B>Render</B> and <B>Supabase</B> host the application and the
            database. They store the data on our behalf and do not use it.
          </li>
          <li style={{ marginBottom: 10 }}>
            <B>Resend</B> receives your email address and name in order to send
            account verification and password reset messages. It receives
            nothing about the person you care for.
          </li>
        </ul>
        <p>
          We do not sell your data. We do not share it for advertising. We do
          not share it with insurers, employers, schools, or anyone else. No
          other Mindris user can see your profile or your sessions.
        </p>

        <H>Information about children</H>
        <p>
          Most Mindris profiles describe a minor. That information is entered by
          an adult caregiver about someone in their care — Mindris is not
          intended for use by children, and we do not knowingly collect
          information directly from anyone under 18.
        </p>
        <p>
          If you are not that person's parent or legal guardian, you should have
          their permission before entering information about them.
        </p>

        <H>How long we keep it</H>
        <p>
          Until you delete it. There is no automatic expiry, because the value
          of the profile is that it accumulates.
        </p>
        <p>
          Deleting your account from Settings permanently removes your account,
          every care profile under it, every session, and every rating. This is
          a real deletion, not a deactivation flag, and it cannot be undone.
        </p>

        <H>Your choices</H>
        <p>
          You can view and edit every field of a profile at any time, delete an
          individual profile, or delete your entire account from within the app.
          You can withdraw consent by deleting your account. If you would rather
          we handled a request directly, email us and we will.
        </p>

        <H>Security</H>
        <p>
          Traffic is encrypted in transit. Passwords are hashed with bcrypt.
          Sessions expire after seven days and are revoked when you change your
          password. Every request for a profile is checked against the account
          that owns it. Requests are rate limited.
        </p>
        <p>
          No system is perfectly secure, and we would rather say that than imply
          otherwise.
        </p>

        <H>THIS IS NOT A MEDICAL DEVICE OR SERVICE</H>
        <p style={{ letterSpacing: '0.3px', lineHeight: 1.95 }}>
          MINDRIS OFFERS CAREGIVER SUPPORT SUGGESTIONS. IT DOES NOT DIAGNOSE,
          TREAT, OR PRESCRIBE, AND IT IS NOT A SUBSTITUTE FOR YOUR MEDICAL TEAM.
          IN AN EMERGENCY, CONTACT EMERGENCY SERVICES.
        </p>

        <H>Changes</H>
        <p>
          We may update this policy from time to time. If a change materially
          affects how your information is collected, used, or shared, we will
          tell you in the app before it takes effect. The effective date above
          always reflects the current version, and you can withdraw consent at
          any time by deleting your account.
        </p>

        <div style={{ marginTop: 56, paddingTop: 24, borderTop: '1px solid #262626' }}>
          <button
            onClick={() => navigate('landing')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: '#6b6b6b', fontSize: 14, fontFamily: 'Inter, sans-serif',
            }}
          >
            ← Back to Mindris
          </button>
        </div>
      </div>
    </div>
  );
}

function H({ children }) {
  return (
    <h2 style={{
      fontSize: 19, fontWeight: 600, color: '#ececec',
      margin: '44px 0 14px', letterSpacing: '-0.3px',
      fontFamily: 'Inter, sans-serif',
    }}>{children}</h2>
  );
}

function B({ children }) {
  return <strong style={{ color: '#ececec', fontWeight: 600 }}>{children}</strong>;
}

function A({ href, children }) {
  return (
    <a href={href} style={{ color: '#0a9c85', textDecoration: 'none' }}>{children}</a>
  );
}
